import * as React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { useUploadSuretyDoc } from '../../../features/surety/hooks';
import { useToast } from '../../../components/ui/Toast';
import { Eye, Upload, FileText } from 'lucide-react';
import type { SuretyNominee, NomineeDocument } from '../../../features/surety/types';

interface SuretyDocumentsListProps {
  activeNominee: SuretyNominee | null;
  onClose: () => void;
  requestId: string;
  canUpdate: boolean;
  onActiveNomineeChange: (nominee: SuretyNominee) => void;
}

const DOC_TYPES = [
  { value: 'PAN', label: 'PAN Card' },
  { value: 'AADHAAR', label: 'Aadhaar Card' },
  { value: 'SALARY_SLIP', label: 'Salary Slip' },
  { value: 'BUSINESS_PROOF', label: 'Business Proof' },
] as const;

export function SuretyDocumentsList({
  activeNominee,
  onClose,
  requestId,
  canUpdate,
  onActiveNomineeChange,
}: SuretyDocumentsListProps) {
  const { toast } = useToast();
  const uploadDocMutation = useUploadSuretyDoc();
  const [previewDoc, setPreviewDoc] = React.useState<{ type: string; url: string } | null>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, docType: NomineeDocument['doc_type']) => {
    if (!activeNominee) return;
    const file = e.target.files?.[0];
    if (!file) return;

    // Unhappy path size check: max 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast({
        type: 'error',
        title: 'File Too Large',
        description: 'File size must be under 5MB.',
      });
      return;
    }

    // Unhappy path format check: PDF, JPEG, PNG
    const supportedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!supportedTypes.includes(file.type)) {
      toast({
        type: 'error',
        title: 'Invalid Format',
        description: 'Only PDF, JPEG, and PNG files are supported.',
      });
      return;
    }

    // Simulate file upload by reference
    const doc: NomineeDocument = {
      doc_type: docType,
      file_ref: `/uploads/${file.name}`,
      uploaded_at: new Date().toISOString(),
    };

    const currentDocs = activeNominee.documents || [];
    const updatedDocs = [...currentDocs.filter((d) => d.doc_type !== docType), doc];

    uploadDocMutation.mutate(
      {
        requestId,
        nomineeId: activeNominee.id,
        documents: updatedDocs,
      },
      {
        onSuccess: () => {
          onActiveNomineeChange({
            ...activeNominee,
            documents: updatedDocs,
            status: 'DOCUMENTS_SUBMITTED',
          });
        },
      }
    );
  };

  return (
    <>
      <Dialog open={activeNominee !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="sm:max-w-lg bg-surface-1">
          <DialogHeader>
            <DialogTitle>Nominee Documents Management</DialogTitle>
            <DialogDescription>
              Review uploaded files or upload missing KYC documents for {activeNominee?.id ? 'Nominee' : ''}.
            </DialogDescription>
          </DialogHeader>

          {activeNominee && (
            <div className="space-y-4 py-2 text-sm">
              <div className="grid grid-cols-1 gap-3">
                {DOC_TYPES.map((dt) => {
                  const doc = activeNominee.documents?.find((d) => d.doc_type === dt.value);

                  return (
                    <div
                      key={dt.value}
                      className="flex items-center justify-between p-3 border border-border rounded-lg bg-surface-2 text-xs"
                    >
                      <div>
                        <p className="font-semibold text-text-primary">{dt.label}</p>
                        {doc ? (
                          <p className="text-text-muted mt-0.5">
                            Uploaded at: {new Date(doc.uploaded_at).toLocaleString()}
                          </p>
                        ) : (
                          <p className="text-danger-text mt-0.5">Missing Document</p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {doc ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setPreviewDoc({ type: dt.label, url: doc.file_ref })}
                          >
                            <Eye className="w-3.5 h-3.5" /> Preview
                          </Button>
                        ) : (
                          canUpdate && (
                            <label className="inline-flex items-center justify-center gap-1.5 font-medium rounded-[6px] transition-all bg-transparent border border-border-strong text-text-primary hover:bg-surface-2 h-7 px-3 text-xs cursor-pointer select-none">
                              <Upload className="w-3.5 h-3.5" /> Upload File
                              <Input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                onChange={(e) => handleFileUpload(e, dt.value)}
                                className="hidden"
                              />
                            </label>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Lightbox */}
      <Dialog open={previewDoc !== null} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="sm:max-w-lg bg-surface-1">
          <DialogHeader>
            <DialogTitle>Document Preview - {previewDoc?.type}</DialogTitle>
          </DialogHeader>
          <div className="bg-surface-3 border border-border rounded-xl p-8 text-center flex flex-col items-center justify-center min-h-[250px] gap-3">
            <FileText className="w-12 h-12 text-accent-500" />
            <p className="text-sm font-semibold text-text-primary">Simulated File Preview</p>
            <p className="font-mono text-xs text-text-secondary select-all">{previewDoc?.url}</p>
            <p className="text-xs text-text-muted max-w-sm mt-1">
              File verified on-disk path matches requirements. Status is DOCUMENTS_SUBMITTED.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setPreviewDoc(null)}>Close Preview</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
